import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Singleton manager for Supabase Realtime connections
 * Prevents duplicate channels and manages connection pooling
 */
class RealtimeConnectionManager {
  private static instance: RealtimeConnectionManager;
  private channels: Map<string, RealtimeChannel> = new Map();
  private connectionCount = 0;
  private maxConnections = 5; // Strictly limited to prevent database exhaustion
  private isDev = false; // Disable all logs in production
  private isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  private channelAccessTime: Map<string, number> = new Map(); // Track last access time
  private pendingCleanup: Set<string> = new Set(); // Channels scheduled for cleanup

  private constructor() {
    // Monitor connection health every 10 seconds for aggressive cleanup
    setInterval(() => {
      this.cleanupStaleChannels();
      // Force cleanup if over limit
      if (this.connectionCount > this.maxConnections) {
        this.forceCleanupExcessChannels();
      }
    }, 10000);
  }

  static getInstance(): RealtimeConnectionManager {
    if (!RealtimeConnectionManager.instance) {
      RealtimeConnectionManager.instance = new RealtimeConnectionManager();
    }
    return RealtimeConnectionManager.instance;
  }

  private log(...args: any[]) {
    // Completely disabled in production
    return;
  }

  private error(...args: any[]) {
    // Completely disabled in production
    return;
  }

  /**
   * Get or create a channel with the given name
   * Returns existing channel if available and healthy
   */
  getChannel(channelName: string, config?: any): RealtimeChannel | null {
    // Cancel any pending cleanup for this channel
    if (this.pendingCleanup.has(channelName)) {
      this.log(`Cancelling pending cleanup for channel "${channelName}"`);
      this.pendingCleanup.delete(channelName);
    }

    // Check if we already have this channel
    const existing = this.channels.get(channelName);

    if (existing) {
      const state = existing.state;
      this.log(`Found existing channel "${channelName}" in state: ${state}`);

      // Return existing channel if it's healthy or joining
      if (state === 'joined' || state === 'joining') {
        // Update access time for LRU tracking
        this.channelAccessTime.set(channelName, Date.now());
        return existing;
      }

      // Clean up unhealthy channel immediately
      // But don't clean up 'errored' channels right away - they might recover
      if (state === 'closed' || state === 'leaving') {
        this.log(`Cleaning up unhealthy channel "${channelName}"`);
        this.removeChannel(channelName);
      } else if (state === 'errored') {
        // Try to unsubscribe and resubscribe instead of removing
        this.log(`Channel "${channelName}" is errored, attempting to recover`);
        try {
          existing.unsubscribe();
          existing.subscribe();
          return existing;
        } catch (err) {
          this.log(`Failed to recover errored channel, removing: ${err}`);
          this.removeChannel(channelName);
        }
      }
    }

    // Check connection limit BEFORE creating new channel
    if (this.connectionCount >= this.maxConnections) {
      this.error(`Connection limit reached (${this.connectionCount}/${this.maxConnections})`);
      // Try to cleanup stale channels first
      this.cleanupStaleChannels();

      // If still at limit, force cleanup of oldest channel
      if (this.connectionCount >= this.maxConnections) {
        this.cleanupOldestChannel();
      }
    }

    // Create new channel with config
    try {
      const channelConfig = config || {
        config: {
          broadcast: {
            self: true, // Must be true for bidirectional chat
            ack: true, // Request acknowledgment for reliable delivery
          },
          presence: {
            key: channelName
          },
          // IMPORTANT: Enable private mode to prevent automatic closure
          private: false
        },
        params: {
          // Keep connection alive with heartbeat
          heartbeat_interval: 10
        }
      };

      // Double-check we don't already have this channel (race condition protection)
      const existingAfterCheck = this.channels.get(channelName);
      if (existingAfterCheck) {
        this.log(`Race condition prevented - channel "${channelName}" already exists`);
        return existingAfterCheck;
      }

      const channel = supabase.channel(channelName, channelConfig);
      this.channels.set(channelName, channel);
      this.channelAccessTime.set(channelName, Date.now()); // Track creation time
      this.connectionCount = this.channels.size; // Use actual size instead of incrementing
      this.log(`Created new channel "${channelName}" (total: ${this.connectionCount})`);
      return channel;
    } catch (error) {
      this.error(`Failed to create channel "${channelName}":`, error);
      return null;
    }
  }

  /**
   * Mark a channel for cleanup after a grace period
   * This allows reuse if the same channel is requested again soon
   */
  scheduleChannelCleanup(channelName: string): void {
    this.log(`Scheduling cleanup for channel "${channelName}"`);
    this.pendingCleanup.add(channelName);

    // Use longer grace period on mobile to handle slower transitions
    const cleanupDelay = this.isMobile ? 15000 : 5000; // 15s for mobile, 5s for desktop

    setTimeout(async () => {
      // Check if channel is still pending cleanup and not reused
      if (this.pendingCleanup.has(channelName)) {
        const channel = this.channels.get(channelName);
        if (channel && channel.state !== 'joined' && channel.state !== 'joining') {
          await this.removeChannel(channelName);
        }
        this.pendingCleanup.delete(channelName);
      }
    }, cleanupDelay);
  }

  /**
   * Remove a channel and clean up resources IMMEDIATELY
   */
  async removeChannel(channelName: string): Promise<void> {
    const channel = this.channels.get(channelName);
    if (!channel) return;

    try {
      await supabase.removeChannel(channel);
      this.channels.delete(channelName);
      this.channelAccessTime.delete(channelName); // Clean up access time
      this.pendingCleanup.delete(channelName); // Remove from pending cleanup
      this.connectionCount = this.channels.size; // Use actual size
      this.log(`Removed channel "${channelName}" (total: ${this.connectionCount})`);
    } catch (error) {
      this.error(`Failed to remove channel "${channelName}":`, error);
    }
  }

  /**
   * Clean up all stale or errored channels
   */
  private async cleanupStaleChannels(): Promise<void> {
    const staleChannels: string[] = [];

    for (const [name, channel] of this.channels) {
      const state = channel.state;
      if (state === 'closed' || state === 'errored') {
        staleChannels.push(name);
      }
    }

    if (staleChannels.length > 0) {
      this.log(`Cleaning up ${staleChannels.length} stale channels`);
      for (const name of staleChannels) {
        await this.removeChannel(name);
      }
    }
  }

  /**
   * Remove the least recently used channel to make room for new ones
   */
  private async cleanupOldestChannel(): Promise<void> {
    let oldestName: string | null = null;
    let oldestTime = Date.now();

    // Find the least recently used channel
    for (const [name, time] of this.channelAccessTime.entries()) {
      if (time < oldestTime) {
        oldestTime = time;
        oldestName = name;
      }
    }

    // If we didn't find any tracked channel, just remove the first one
    if (!oldestName) {
      [oldestName] = this.channels.keys();
    }

    if (oldestName) {
      this.log(`Removing LRU channel "${oldestName}" (last accessed: ${new Date(oldestTime).toISOString()})`);
      await this.removeChannel(oldestName);
    }
  }

  /**
   * Get current connection statistics
   */
  getStats(): {
    activeConnections: number;
    maxConnections: number;
    channels: { name: string; state: string }[];
  } {
    const channels = Array.from(this.channels.entries()).map(([name, channel]) => ({
      name,
      state: channel.state,
    }));

    return {
      activeConnections: this.connectionCount,
      maxConnections: this.maxConnections,
      channels,
    };
  }

  /**
   * Check if a specific channel exists and get its state
   * @param channelName The name of the channel to check
   * @returns The channel state or null if not found
   */
  getChannelState(channelName: string): string | null {
    const channel = this.channels.get(channelName);
    return channel ? channel.state : null;
  }

  /**
   * Check if a channel is healthy (joined or joining)
   * @param channelName The name of the channel to check
   * @returns true if channel is healthy, false otherwise
   */
  isChannelHealthy(channelName: string): boolean {
    const state = this.getChannelState(channelName);
    return state === 'joined' || state === 'joining';
  }

  /**
   * Force cleanup excess channels when over limit
   */
  private async forceCleanupExcessChannels(): Promise<void> {
    const excessCount = this.connectionCount - this.maxConnections;
    if (excessCount <= 0) return;

    const channelsToRemove: string[] = [];
    const sortedChannels = Array.from(this.channelAccessTime.entries())
      .sort((a, b) => a[1] - b[1]); // Sort by access time, oldest first

    for (let i = 0; i < excessCount && i < sortedChannels.length; i++) {
      channelsToRemove.push(sortedChannels[i][0]);
    }

    for (const name of channelsToRemove) {
      await this.removeChannel(name);
    }
  }

  /**
   * Clean up all channels (useful for logout or app shutdown)
   */
  async cleanup(): Promise<void> {
    this.log('Cleaning up all channels');

    // First, clean up all Supabase channels (even ones we're not tracking)
    const allSupabaseChannels = supabase.getChannels();
    for (const channel of allSupabaseChannels) {
      try {
        await supabase.removeChannel(channel);
      } catch (error) {
        this.error(`Failed to remove Supabase channel:`, error);
      }
    }

    // Then clean up our tracked channels
    this.channels.clear();
    this.channelAccessTime.clear();
    this.pendingCleanup.clear();
    this.connectionCount = 0;

    this.log('All channels cleaned up');
  }
}

export default RealtimeConnectionManager.getInstance();